import { useCallback } from 'react';

import type { TranslationKey } from '@change-corgi/core/i18n';
import { useI18n } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';

import type { PlatformDataMap } from 'src/app/pages/starterDashboardV2/shared/hooks';

import { componentsFactory, useActionsFactory } from '../factories';
import type { SharingModalProps } from '../types';
import { getFullPostTypeForTracking } from '../utils/fullPostType';

type HookReturnType = {
	data: ReturnType<typeof componentsFactory>;
	actions: ReturnType<typeof useActionsFactory> & {
		trackView: () => void;
	};
};

export const VIEW_EVENT_NAME = 'petition_manage_ready_to_share_post_modal_view';

export function useSharingModal(props: SharingModalProps, dynamicData: PlatformDataMap): HookReturnType {
	const { translate } = useI18n();
	const track = useTracking();
	const { primaryAction, secondaryAction } = useActionsFactory(props, dynamicData);
	const components = useCallback(() => {
		return componentsFactory(props, dynamicData, translate as (key: string | TranslationKey) => string);
	}, [props, dynamicData, translate]);

	const actions = useCallback(() => {
		return {
			primaryAction,
			secondaryAction,
		};
	}, [primaryAction, secondaryAction]);

	const recommendedGroup = 'recommendedGroup' in props ? props.recommendedGroup : undefined;
	const { platform, postType } = props;

	const trackView = useCallback(() => {
		const { context, viewContext } = props.trackingProps;
		const location = getFullPostTypeForTracking(platform, postType, !!recommendedGroup);

		void track(VIEW_EVENT_NAME, {
			context,
			view_context: viewContext,
			location,
			content: 'show_draft_post',
		});
	}, [props.trackingProps, platform, postType, recommendedGroup, track]);

	return {
		data: {
			...components(),
		},
		actions: {
			...actions(),
			trackView,
		},
	};
}
